import { Button, Heading, Text, Toggle } from '@mallpay/orbis';
import React, { useState } from 'react';

import { BnplLimitProduct, BnplProductType } from '@skippay/core/@types/myProfile';
import FormattedCurrency from '@skippay/core/components/FormattedCurrency';
import { usePageview } from '@skippay/core/components/GoogleAnalytics';
import Link from '@skippay/core/components/Link';
import useInitialCountryLocale from '@skippay/core/hooks/useInitialCountryLocale';
import { FormattedMessage } from '@skippay/core/i18n/intl';

import { OVERVIEW, ROUTES } from 'constants/routes';
import { FooterButtons } from 'layout/MinimalLayout/components/FooterButtons';

interface Props {
	products: BnplLimitProduct[];
	isConfirming: boolean;
	onConfirm: (types: BnplProductType[]) => void;
}

function ProductSelection(props: Props): JSX.Element {
	const { products, isConfirming, onConfirm } = props;
	const { locale } = useInitialCountryLocale();
	usePageview({ pTitle: 'bnpl_limit_management_product_selection' });

	const deferredPayment = products.find((product) => product.type === 'DEFERRED_PAYMENT');
	const payInThree = products.find((product) => product.type === 'PAY_IN_THREE');
	const visibleTypes = products.map((product) => product.type);

	const [enabledByType, setEnabledByType] = useState<Record<BnplProductType, boolean>>({
		DEFERRED_PAYMENT: false,
		PAY_IN_THREE: false,
	});

	const toggle = (type: BnplProductType) => () =>
		setEnabledByType((prev) => ({ ...prev, [type]: !prev[type] }));

	const selectedTypes = visibleTypes.filter((type) => enabledByType[type]);
	const canConfirm = selectedTypes.length > 0;

	return (
		<section className="mx-auto flex max-w-[640px] flex-col gap-8 pt-6 pb-8">
			<div className="flex flex-col pb-4">
				<Heading as="h1" className="text-center">
					<FormattedMessage
						id="BnplLimitManagement.ProductSelection.title"
						defaultMessage="Nakupujte pohodlně i dál"
					/>
				</Heading>

				<div>
					<Text as="div" className="mt-3 text-center">
						<FormattedMessage
							id="BnplLimitManagement.ProductSelection.description"
							defaultMessage="Odložené platby se brzy změní. Nově budeme mít povinnost u každého nákupu ověřit vaši úvěruschopnost.{br}{br} Pokud chcete i dál nakupovat na jeden klik bez ověřování, nastavte si Limity pro své služby. "
							values={{
								br: <br />,
							}}
						/>
					</Text>
				</div>

				<Heading as="h1" className="pt-8 pb-6 text-center">
					<FormattedMessage id="BnplLimitManagement.limits.title" defaultMessage="Vaše limity" />
				</Heading>

				<div className="mt-1.5 flex flex-col gap-4 rounded-lg bg-blue-100 p-4 md:items-center">
					<div className="flex flex-col gap-4">
						{deferredPayment && (
							<div className="flex flex-row gap-4">
								<Toggle
									checked={enabledByType.DEFERRED_PAYMENT}
									disabled={false}
									onChange={toggle('DEFERRED_PAYMENT')}
								/>
								<Text as="div" className="mb-0! text-center">
									<FormattedMessage
										id="BnplLimitManagement.ProductSelection.toggleTitleDeferredPayment"
										defaultMessage="Odložená platba: {limit}"
										values={{
											limit: (
												<FormattedCurrency
													testId="bnplDeferredPaymentLimit"
													{...deferredPayment.proposedLimit}
												/>
											),
										}}
									/>
								</Text>
							</div>
						)}

						{payInThree && (
							<div className="flex flex-row gap-4">
								<Toggle
									checked={enabledByType.PAY_IN_THREE}
									disabled={false}
									onChange={toggle('PAY_IN_THREE')}
								/>
								<Text as="div" className="text-center">
									<FormattedMessage
										id="BnplLimitManagement.ProductSelection.toggleTitlePayInThree"
										defaultMessage="Nákup na Třetinu: {limit}"
										values={{
											limit: (
												<FormattedCurrency
													testId="bnplPayInThreeLimit"
													{...payInThree.proposedLimit}
												/>
											),
										}}
									/>
								</Text>
							</div>
						)}
					</div>

					<div className="mt-6">
						<Text as="div" size="small" className="mb-0! text-center">
							<FormattedMessage
								id="BnplLimitManagement.ProductSelection.documents"
								defaultMessage="Pokračováním souhlasíte s <terms_link>Podmínkami Odložené platby a Nákupu na Třetinu</terms_link> a <pricelist_link>Ceníkem</pricelist_link>"
								values={{
									terms_link: (title) => (
										<Link
											onClick={() => {
												window.dataLayer?.push({
													event: 'click_terms',
													analyticsId: 'bnplLimitsTerms',
												});
											}}
											isExternal
											target="_blank"
											to={ROUTES(locale).conditions}
											testId="bnplLimitsTerms"
											analyticsId="bnplLimitsTerms"
										>
											{title}
										</Link>
									),
									pricelist_link: (title) => (
										<Link
											onClick={() => {
												window.dataLayer?.push({
													event: 'click_pricelist',
													analyticsId: 'bnplLimitsPricelist',
												});
											}}
											isExternal
											target="_blank"
											to={ROUTES(locale).pricing}
											testId="bnplLimitsPricelist"
											analyticsId="bnplLimitsPricelist"
										>
											{title}
										</Link>
									),
								}}
							/>
						</Text>
					</div>
				</div>
			</div>

			<FooterButtons
				leftContent={
					<Button
						testId="bnplSetLimitsLater"
						analyticsParam="bnplSetLimitsLater"
						variant="secondary"
						disabled={false}
						to={OVERVIEW}
					>
						<FormattedMessage
							id="BnplLimitManagement.ProductSelection.submitLater"
							defaultMessage="Dokončit později"
						/>
					</Button>
				}
				content={
					<Button
						testId="bnplSetLimits"
						analyticsParam="bnplSetLimits"
						variant="primary"
						disabled={!canConfirm || isConfirming}
						onClick={() => onConfirm(selectedTypes)}
					>
						<FormattedMessage
							id="BnplLimitManagement.ProductSelection.submit"
							defaultMessage="Nastavit limity"
						/>
					</Button>
				}
			/>
		</section>
	);
}

export default ProductSelection;
